<template>
  <div class="resizable-event">
    <BaseDraggableResizable
      v-if="event"
      ref="baseDraggableResizableEl"
      :active="true"
      :class="{ resizing }"
      :draggable="false"
      :grid="[10, 10]"
      :h="height"
      :handles="['bm']"
      :max-height="maxHeight || undefined"
      :prevent-deactivation="true"
      :enable-native-drag="true"
      class="resizable-block"
      w="auto"
      :z="3"
      @resizestop="onResizeStop"
      @resizing="onResizing"
    >
      <EventElement
        :base="base"
        :event="wrappedEvent"
        :given-task="givenTask"
        :dragging-over="draggingOver"
        :dragged-event="draggedEvent"
        :is-recording="isRecording"
        :resizable-duration="duration"
        :is-selected="isSelected"
        @select="(id) => $emit('select', id)"
        @unselect="$emit('unselect')"
      />
      <template #bm>
        <div v-if="!draggingOver" class="handle-feedback" />
      </template>
    </BaseDraggableResizable>
  </div>
</template>

<script lang="ts">
import { add } from "date-fns";
import { isEqual } from "lodash-es";
import { computed, defineComponent, PropType, ref, toRefs, watch } from "vue";

import type { Event, FullTask } from "@ctrl/controller-rpc";

import { TemporaryEvent } from "@/components/agenda/dnd";
import {
  durationOfPixels,
  useHeightOfDuration,
} from "@/components/agenda/pixels";
import BaseDraggableResizable, {
  Resize,
} from "@/components/base/BaseDraggableResizable.vue";
import * as inject from "@/components/inject";
import { safeUpdateEvent } from "@/components/modal";

import { getHTMLElement } from "../utils";
import EventElement from "./Event.vue";

export default defineComponent({
  components: {
    BaseDraggableResizable,
    EventElement,
  },
  props: {
    base: { type: Number, required: true },
    draggedEvent: {
      type: Object as PropType<TemporaryEvent>,
      required: false,
    },
    draggingOver: { type: Boolean, required: true },
    event: { type: Object as PropType<Event>, required: true },
    isRecording: { type: Boolean, required: false, default: false },
    isSelected: { type: Boolean, required: true },
    maxEndDate: { type: Object as PropType<Date>, required: false },
    maxStartDate: { type: Object as PropType<Date>, required: false },
    // Forwarded to EventElement: see the `givenTask` prop of Event.vue.
    givenTask: {
      type: Object as PropType<FullTask | null>,
      required: false,
      default: undefined,
    },
  },
  emits: {
    forceRerender: () => true,
    scrollBottom: (_: boolean) => true,
    select: (_id: string) => true,
    unselect: () => true,
  },
  setup(props, { emit }) {
    const _DEFAULT_HEIGHT = 40;
    const clock = inject.clock();
    const multipleEventsModal = inject.multipleEventsModal();
    const simpleModal = inject.simpleModal();
    const { event, maxEndDate } = toRefs(props);
    const controller = inject.controller();
    const wrappedEvent = ref();
    const resizing = ref(false);
    const baseDraggableResizableEl =
      // @ts-ignore
      ref<InstanceType<typeof BaseDraggableResizable>>();
    watch(event, (value) => (wrappedEvent.value = { ...value }), {
      immediate: true,
    });
    const { duration, height } = useHeightOfDuration(
      wrappedEvent,
      clock,
      _DEFAULT_HEIGHT
    );
    const maxHeight = computed(() => {
      if (!maxEndDate.value) return null;
      const { height } = useHeightOfDuration(
        ref({ start: wrappedEvent.value.start, end: maxEndDate.value }),
        clock,
        _DEFAULT_HEIGHT
      );
      return height.value;
    });
    const onResizing = (
      _x: number,
      _y: number,
      _width: number,
      height: number
    ) => {
      resizing.value = true;
      wrappedEvent.value.end = add(
        wrappedEvent.value.start,
        durationOfPixels(height, _DEFAULT_HEIGHT)
      );
      const windowRect = document.body.getBoundingClientRect();
      const baseEl = baseDraggableResizableEl.value;
      if (!baseEl) return;
      const eventRect = getHTMLElement(baseEl).getBoundingClientRect();
      emit("scrollBottom", eventRect.bottom + 50 > windowRect.bottom);
    };
    const onResizeStop = (data: Resize | null) => {
      if (data) {
        const { left, top, width, height } = data;
        onResizing(left, top, width, height);
        resizing.value = false;
        if (
          !isEqual(event.value.start, wrappedEvent.value.start) ||
          !isEqual(event.value.end, wrappedEvent.value.end)
        )
          safeUpdateEvent(
            "update",
            event.value.id,
            { controller, simpleModal, multipleEventsModal },
            {
              patch: {
                id: wrappedEvent.value.id,
                time: {
                  start_time: wrappedEvent.value.start,
                  end_time: wrappedEvent.value.end,
                },
              },
            }
          );
      } else {
        // Resize has been canceled
        resizing.value = false;
      }
    };
    return {
      baseDraggableResizableEl,
      duration,
      height,
      maxHeight,
      onResizeStop,
      onResizing,
      resizing,
      wrappedEvent,
    };
  },
});
</script>

<style lang="scss" scoped>
.resizable-event {
  display: flex;
  flex-direction: column;
  width: 100%;

  .resizable-block {
    position: relative;
    border: none;
    min-height: 40px;

    :deep(.handle-bm) {
      width: 100%;
      inset-inline-start: 4px;
      background-color: transparent;
      border: none;
    }
  }

  .event-duration {
    @extend %font-caption-regular;
    white-space: nowrap;
    @include themed() {
      color: t($grey-40);
    }
  }

  :deep(.handle.handle-bm) {
    bottom: -6px;
    cursor: ns-resize;
    height: 12px;
    position: absolute;
    width: 100%;
    z-index: $z-index-content;
  }
}
</style>
